import Card from '@/components/Common/Card'
import React, { useContext, useEffect, useState } from 'react'

import { Box } from '@mui/material'

import Link from '../../components/Common/Link'
import ProductImg from './ProductImg'

import getRoutes from '@/lib/getRoutes'
import getProductPrice from '@/lib/getProductPrice'
import getProductQuantityPcb from '@/lib/getProductQuantityPcb'
import ProfileContext from '@/components/Context/ProfileContext'
import COLORS from '@/src/colors'
import ProductAddToBasket from '@/components/Product/ProductAddToBasket'
import ProductQtyInput, { getInitialDisplayedQty } from '@/components/Product/ProductQtyInput'
import BrandNameLink from '@/components/Brands/BrandNameLink'
import FilterByHover, { FACET_BRAND } from '@/components/Search/FilterByHover'
import ClampText from '../ClampText'
import HighlightText from '@/components/Search/HighlightText'
import PrecommandeBadge from '@/components/Product/PrecommandeBadge'
import { recordSearchClick } from '@/lib/sessionTracking'

type Props = {
  product: any
  sizeProduct?: string
  /** Terme de recherche actif (page de resultats) — pour le clic-tracking session */
  searchQuery?: string
  /** Position (1-based) du produit dans les resultats de recherche */
  searchRank?: number
  /** Active le bouton "Filtrer par..." au survol (pages avec panneau de facettes) */
  facetFilterEnabled?: boolean
}

const ModernProduct: React.FC<Props> = ({
  product,
  sizeProduct = 'small',
  searchQuery,
  searchRank,
  facetFilterEnabled = false,
}) => {
  const [qtyCart, setQtyCart] = useState<number>(0)
  const { isAuthenticated } = useContext(ProfileContext)

  useEffect(() => {
    if (product) {
      const qty = getProductQuantityPcb(product, 0)
      // getProductQuantityPcb retourne 0 pour les produits multi-paliers, ce qui
      // grisait le bouton panier alors que le selecteur affiche une quantite :
      // on aligne l'etat parent sur la quantite reellement affichee
      setQtyCart(Number.isFinite(qty) && qty > 0 ? qty : getInitialDisplayedQty(product))
    }
  }, [product?.seo_name])

  const { percent, isPromotion, promotionLimited } = getProductPrice(product, qtyCart)

  const urlProduct = getRoutes({
    route: 'Product',
    values: { '[slug]': product.seo_name },
  })

  // Session analytics : memorise {query, rank} au clic sur un resultat de
  // recherche — le fetch de la fiche produit enverra X-Pharme-Src/Rank (<30 s)
  const handleSearchResultClick = (): void => {
    if (searchRank && searchRank > 0) {
      recordSearchClick(searchQuery ?? '', searchRank)
    }
  }

  return (
    <Card className="p-0 rounded-[10px] flex flex-col h-full min-h-full max-h-full relative shadow-md overflow-hidden border border-gray-100">
      {(isPromotion || promotionLimited) && (
        <div
          className="text-white py-1 px-2.5 rounded-[20px] text-sm font-bold absolute top-2.5 left-2.5 z-10"
          style={{ backgroundColor: COLORS.orange }}
        >
          -{percent}%
        </div>
      )}

      {product.precommande === 'Y' && (
        <div className="absolute top-2.5 right-2.5 z-10">
          <PrecommandeBadge variant="compact" availSince={product.avail_since} />
        </div>
      )}

      <div
        className="flex justify-center items-center p-5 bg-white"
        onClick={handleSearchResultClick}
      >
        <Link href={urlProduct.href} as={urlProduct.as} data-testid={'viewProduct'}>
          <ProductImg product={product} size={sizeProduct} />
        </Link>
      </div>

      <div className="flex-grow flex flex-col px-4">
        <FilterByHover
          facetName={FACET_BRAND}
          facetValue={product.brand_name}
          enabled={facetFilterEnabled}
        >
          <BrandNameLink product={product} enableHighlight />
        </FilterByHover>
        <div
          className="font-semibold text-[#213390] mb-1 leading-tight"
          onClick={handleSearchResultClick}
        >
          <Link href={urlProduct.href} as={urlProduct.as}>
            <ClampText clamp={2}>
              <HighlightText text={product.product} />
            </ClampText>
          </Link>
        </div>
        <div className="italic text-xs text-gray-500 mb-0.5">
          EAN : <HighlightText text={product.product_code} />
        </div>
      </div>

      {!!isAuthenticated && (
        <Box px={0} pb={0}>
          <div className="mt-0">
            <ProductQtyInput
              product={product}
              initialQty={qtyCart || getProductQuantityPcb(product, 0) || 1}
              onChange={setQtyCart}
              displayMode="row"
              showInfoBar={false}
              compact
            />
          </div>

          <div className="flex justify-end items-center mt-1.5">
            <ProductAddToBasket qtyCart={qtyCart} product={product} list compactMode compact />
          </div>
        </Box>
      )}

      {product.precommande === 'Y' && (
        <div className="mt-1 mb-2 px-2 flex flex-col text-center leading-tight">
          {!!product.avail_since && product.avail_since > 0 && (
            <span className="text-xs text-amber-700 italic">
              {t({ id: 'web_precommande_delivery_on' }).replace(
                '{date}',
                new Date(product.avail_since * 1000).toLocaleDateString('fr-FR')
              )}
            </span>
          )}
          <span className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
            {t({ id: 'web_precommande_only' })}
          </span>
        </div>
      )}
    </Card>
  )
}

export default ModernProduct
